import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import useApp from '@/composables/app'

type User = {
  email: string
  id: number
  name: string
  role: string
}

const getStorage = () => (typeof window !== 'undefined' ? window.localStorage : null)

export const useAuthStore = defineStore('auth', () => {
  const { storagePrefix } = useApp()
  const STORAGE_KEY = `${storagePrefix}session`

  const storage = getStorage()
  const saved = storage ? storage.getItem(STORAGE_KEY) : null

  let parsed: { token: string; user: User } | null = null

  if (saved) {
    try {
      parsed = JSON.parse(saved) as { token: string; user: User }
    } catch {
      parsed = null
    }
  }

  const user = ref<User | null>(parsed?.user ?? null)
  const token = ref<string | null>(parsed?.token ?? null)

  const isAuthenticated = computed(() => Boolean(user.value && token.value))

  const persist = () => {
    if (!storage) {
      return
    }

    if (user.value && token.value) {
      storage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          user: user.value,
          token: token.value
        })
      )
    } else {
      storage.removeItem(STORAGE_KEY)
    }
  }

  function authenticate(payload: { token: string; user: User }): void {
    token.value = payload.token
    user.value = payload.user
    persist()
  }

  function logout(): void {
    token.value = null
    user.value = null
    persist()
  }

  return {
    user,
    token,
    isAuthenticated,
    authenticate,
    logout
  }
})
